// src/pages/AdminQuestions.jsx
// STATUS: active | UPDATED: 2026-09-17
// LOG:
//   2026-09-17  The coach can now raise things of its own with him, so this
//               page shows both halves of that conversation rather than only
//               the half we started.
//   2026-09-15  New. The answers Jazz has already given, and what we did with
//               them.
//
// WHY THIS EXISTS
//
// `admin_questions` had two readers. The coach, which builds Jazz an agenda from
// the rows where status is 'open', and the dock badge, which counts the same
// rows. Both of them stop caring the instant he answers.
//
// So the lifecycle was: we ask, he answers, the row goes quiet, and the work his
// answer unblocked is represented nowhere. On 2026-09-15 that turned out to have
// cost sixteen days on two answers. He had defined the 300% Rule and named the
// four numbers he measures, and the coach was still refusing to say the first
// and guessing at the second.
//
// The important list on this page is the middle one: answered, not yet built.
// It is the only list here that is a debt. Open questions are his to clear and
// he clears them by talking to the coach. This list is ours.

import { useCallback, useEffect, useState } from 'react'
import { Box, Button, Flex, HStack, VStack, Text, Badge, Spinner, Alert, AlertIcon, Textarea } from '@chakra-ui/react'
import { CircleCheck, CircleDashed, CircleAlert, MessageSquare } from 'lucide-react'
import { supabase } from '../lib/supabase'

const SELECT = 'id, key, topic, question, unblocks, status, answer, answered_at, applied_at, applied_note, sort'

function days(iso) {
  if (!iso) return null
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
}

function since(iso) {
  const d = days(iso)
  if (d === null) return ''
  if (d === 0) return 'today'
  if (d === 1) return 'yesterday'
  return `${d} days ago`
}

// Answered and not built is the only state that gets louder with time, because
// it is the only one where waiting costs something.
function Debt({ q, onApplied }) {
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const waited = days(q.answered_at)

  const apply = useCallback(async () => {
    setSaving(true)
    setError(null)
    const { error: err } = await supabase
      .from('admin_questions')
      .update({ applied_at: new Date().toISOString(), applied_note: note.trim() || null })
      .eq('id', q.id)
    setSaving(false)
    if (err) setError(err.message)
    else onApplied(q.id, note.trim())
  }, [note, q.id, onApplied])

  return (
    <Box borderTop="1px solid" borderColor="line" py={6}>
      <Flex justify="space-between" align="flex-start" gap={4} wrap="wrap" mb={2}>
        <HStack spacing={2} color="inkMuted" fontSize="sm">
          <CircleAlert size={15} />
          <Text>{q.topic}</Text>
        </HStack>
        {waited !== null && (
          <Badge variant="subtle" colorScheme={waited > 7 ? 'red' : 'gray'}>
            answered {since(q.answered_at)}
          </Badge>
        )}
      </Flex>

      <Text fontSize="body" color="inkMuted" mb={3} maxW="measure">
        {q.question}
      </Text>

      {/* His words, unedited. Paraphrasing an answer on the screen where we
          decide what to build with it is how the answer gets lost twice. */}
      <Box borderLeft="3px solid" borderColor="accent" pl={4} py={1} mb={3}>
        <Text fontSize="lg" lineHeight="1.5">
          {q.answer}
        </Text>
      </Box>

      {q.unblocks && (
        <Text fontSize="sm" color="inkDim" mb={4} maxW="measure">
          Blocked while this sits: {q.unblocks}
        </Text>
      )}

      <VStack align="stretch" spacing={2} maxW="measure">
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What changed because of this answer, in one line"
          rows={2}
          fontSize="body"
        />
        <HStack>
          <Button size="sm" onClick={apply} isLoading={saving} isDisabled={!note.trim()}>
            Mark built
          </Button>
          <Text fontSize="sm" color="inkDim">
            The note is the point. A date on its own tells the next person nothing.
          </Text>
        </HStack>
        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}
      </VStack>
    </Box>
  )
}

function Settled({ q }) {
  return (
    <Box borderTop="1px solid" borderColor="line" py={5}>
      <HStack spacing={2} color="inkMuted" fontSize="sm" mb={1}>
        <CircleCheck size={15} />
        <Text>{q.topic}</Text>
        <Text color="inkDim">• built {since(q.applied_at)}</Text>
      </HStack>
      <Text fontSize="body" mb={1} maxW="measure">
        {q.answer}
      </Text>
      {q.applied_note && (
        <Text fontSize="sm" color="inkDim" maxW="measure">
          {q.applied_note}
        </Text>
      )}
    </Box>
  )
}

function Waiting({ q }) {
  return (
    <Box borderTop="1px solid" borderColor="line" py={5}>
      <HStack spacing={2} color="inkMuted" fontSize="sm" mb={1}>
        <CircleDashed size={15} />
        <Text>{q.topic}</Text>
      </HStack>
      <Text fontSize="body" maxW="measure">
        {q.question}
      </Text>
      {q.unblocks && (
        <Text fontSize="sm" color="inkDim" mt={1} maxW="measure">
          {q.unblocks}
        </Text>
      )}
    </Box>
  )
}

// What the coach put to him, and what he did with it. Nothing here is ever
// taught from, which is the whole reason it is allowed to exist. The table can
// be dropped whole without touching a word of his method.
function Raised({ o }) {
  const verdict = {
    accepted: { word: 'he took it', tone: 'inkMuted' },
    rejected: { word: 'he turned it down', tone: 'inkDim' },
    parked: { word: 'parked', tone: 'inkDim' },
    no_answer: { word: 'no answer yet', tone: 'inkDim' },
  }[o.reaction] || { word: o.reaction, tone: 'inkDim' }

  return (
    <Box borderTop="1px solid" borderColor="line" py={5}>
      <HStack spacing={2} color="inkMuted" fontSize="sm" mb={1} flexWrap="wrap">
        <MessageSquare size={15} />
        <Text>{o.kind}</Text>
        {o.step_number && <Text color="inkDim">• step {o.step_number}</Text>}
        <Text color={verdict.tone}>• {verdict.word}</Text>
      </HStack>
      <Text fontSize="body" fontWeight={600} maxW="measure">
        {o.title}
      </Text>
      <Text fontSize="body" color="inkMuted" maxW="measure" mt={0.5}>
        {o.body}
      </Text>
      {o.reaction_note && (
        <Text fontSize="body" color="ink" maxW="measure" mt={2} borderLeft="2px solid" borderColor="line" pl={3}>
          {o.reaction_note}
        </Text>
      )}
      {/* No basis means it was freelancing, which is the thing worth spotting. */}
      <Text fontSize="sm" color={o.basis ? 'inkDim' : 'accent'} mt={2} maxW="measure">
        {o.basis ? `From: ${o.basis}` : 'No basis recorded'}
      </Text>
    </Box>
  )
}

export default function AdminQuestions() {
  const [state, setState] = useState({ loading: true, rows: [], seen: [], error: null })

  useEffect(() => {
    let alive = true
    Promise.all([
      supabase.from('admin_questions').select(SELECT).order('sort'),
      supabase
        .from('coach_observations')
        .select('id, kind, step_number, title, body, basis, reaction, reaction_note, created_at')
        .order('created_at', { ascending: false })
        .limit(50),
    ]).then(([questions, observations]) => {
      if (!alive) return
      setState({
        loading: false,
        rows: questions.data || [],
        seen: observations.data || [],
        error: questions.error ? questions.error.message : null,
      })
    })
    return () => {
      alive = false
    }
  }, [])

  const onApplied = useCallback((id, note) => {
    setState((s) => ({
      ...s,
      rows: s.rows.map((r) =>
        r.id === id ? { ...r, applied_at: new Date().toISOString(), applied_note: note || null } : r
      ),
    }))
  }, [])

  const answered = state.rows.filter((r) => r.status === 'answered')
  const debt = answered.filter((r) => !r.applied_at)
  const settled = answered.filter((r) => r.applied_at)
  const open = state.rows.filter((r) => r.status === 'open')

  return (
    <VStack align="stretch" spacing={8} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          What Jazz has told us
        </Text>
        <Text textStyle="pageTitle">Questions</Text>
      </Box>

      {state.loading && (
        <HStack spacing={3} color="inkMuted">
          <Spinner size="sm" />
          <Text fontSize="body">Loading</Text>
        </HStack>
      )}

      {state.error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      {!state.loading && !state.error && (
        <>
          <Box>
            <Text fontSize="lg" fontWeight={600} mb={1}>
              Answered, not built
            </Text>
            <Text fontSize="body" color="inkMuted" mb={2} maxW="measure">
              {debt.length === 0
                ? 'Nothing. Every answer he has given has changed something.'
                : 'He has already told us. These are ours, not his.'}
            </Text>
            {debt.map((q) => (
              <Debt key={q.id} q={q} onApplied={onApplied} />
            ))}
          </Box>

          {open.length > 0 && (
            <Box>
              <Text fontSize="lg" fontWeight={600} mb={1}>
                Still with him
              </Text>
              <Text fontSize="body" color="inkMuted" mb={2} maxW="measure">
                The coach raises these itself when he is in there. Nothing to chase by hand.
              </Text>
              {open.map((q) => (
                <Waiting key={q.id} q={q} />
              ))}
            </Box>
          )}

          <Box>
            <Text fontSize="lg" fontWeight={600} mb={1}>
              What the coach raised with him
            </Text>
            <Text fontSize="body" color="inkMuted" mb={2} maxW="measure">
              {state.seen.length === 0
                ? 'Nothing yet. The coach only writes one of these down once it has actually said it to him.'
                : 'Its own observations, not his words. Nothing here is taught from and all of it is disposable.'}
            </Text>
            {state.seen.map((o) => (
              <Raised key={o.id} o={o} />
            ))}
          </Box>

          {settled.length > 0 && (
            <Box>
              <Text fontSize="lg" fontWeight={600} mb={1}>
                Built
              </Text>
              {settled.map((q) => (
                <Settled key={q.id} q={q} />
              ))}
            </Box>
          )}
        </>
      )}
    </VStack>
  )
}
